import React from 'react';
// @mui
import {
  Box,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Card,
  CardContent,
  Pagination,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment,
  Fade
} from '@mui/material';

// @iconify
import { Icon } from '@iconify/react';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import addFill from '@iconify/icons-eva/plus-fill';
// components
import { TokenCard } from './index';
import { Loader } from 'src/components/Loader';
// constants

//hooks
import { useDropdown } from 'src/hooks/useDropdown.hook';
import { debounceFunc } from 'src/utils/helper';

const TokenList = ({
  tokens = [],
  loading,
  error,
  selectedTokenIds = [],
  searchTerm = '',
  typeFilter = 'All Types',
  statusFilter = 'All Status',
  pagination = { offset: 0, limit: 50, total: 0 },
  emptyMessage = "No tokens found",
  onSearchChange,
  onTypeFilterChange,
  onStatusFilterChange,
  onTokenSelect,
  onTokenView,
  onTokenEdit,
  onTokenDelete,
  onCreateToken,
  onRefresh,
  onPageChange,
  onPageSizeChange
}) => {
  const { data: tokenTypes } = useDropdown("token_type");
  const { data: tokenStatus } = useDropdown("token_status");

  const handlePageChange = (event, page) => {
    onPageChange?.(page - 1); // Convert to 0-based index
  };

  const handlePageSizeChange = (event) => {
    onPageSizeChange?.(parseInt(event.target.value, 10));
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header Section with Filters */}
      <Card sx={{ mb: 4, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 3 }}>
          {/* Header Row */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                OCPI Tokens
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Tooltip title="Refresh">
                <IconButton onClick={onRefresh} color="primary">
                  <Icon icon={refreshFill} width={20} height={20} />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<Icon icon={addFill} width={20} height={20} />}
                onClick={onCreateToken}
              >
                Create Token
              </Button>
            </Stack>
          </Stack>

          {/* Filter Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Icon icon="eva:options-2-fill" style={{ fontSize: '20px', color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Filters & Search
            </Typography>
          </Box>

          {/* Filter Controls */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search Box */}
            <TextField
              size="small"
              placeholder="Search tokens..."
              onChange={(e) => debounceFunc(() => onSearchChange?.(e.target.value), 800)()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="eva:search-fill" />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                minWidth: 250,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper'
                }
              }}
            />

            {/* Token Type Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Token Type</InputLabel>
              <Select
                value={typeFilter}
                label="Token Type"
                onChange={(e) => onTypeFilterChange?.(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper'
                  }
                }}
              >
                {tokenTypes && tokenTypes.map((type) => (
                  <MenuItem key={type.id} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => onStatusFilterChange?.(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper'
                  }
                }}
              >
                {tokenStatus && tokenStatus.map((status) => (
                  <MenuItem key={status.id} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Clear Filters */}
            {(searchTerm || typeFilter !== 'All Types' || statusFilter !== 'All Status') && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  onSearchChange?.('');
                  onTypeFilterChange?.('All Types');
                  onStatusFilterChange?.('All Status');
                }}
                startIcon={<Icon icon="eva:refresh-fill" />}
                sx={{
                  borderColor: 'text.secondary',
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main'
                  }
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Select All and Bulk Actions */}
      {/* {tokens.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              p: 2,
              background: selectedTokenIds.length > 0 
                ? 'linear-gradient(135deg, #e8f5e8 0%, #f0f9f0 100%)'
                : 'linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%)',
              borderRadius: 2,
              border: '1px solid',
              borderColor: selectedTokenIds.length > 0 ? 'success.light' : 'grey.300',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              width: '100%'
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ width: '100%' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={selectedTokenIds.length === tokens.length && tokens.length > 0}
                    indeterminate={selectedTokenIds.length > 0 && selectedTokenIds.length < tokens.length}
                    onChange={onSelectAll}
                    color="success"
                    size="small"
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: selectedTokenIds.length > 0 ? 'success.dark' : 'text.secondary' }}>
                    {selectedTokenIds.length === tokens.length && tokens.length > 0 ? 'Deselect All' : 'Select All'}
                  </Typography>
                  {selectedTokenIds.length > 0 && (
                    <Chip
                      label={`${selectedTokenIds.length} selected`}
                      color="primary"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>

                {selectedTokenIds.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Icon icon="eva:power-fill" />}
                      onClick={onActivateSelected}
                      sx={{
                        bgcolor: 'white',
                        color: 'text.primary',
                        borderColor: 'grey.300',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          borderColor: 'grey.400',
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Activate
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Icon icon="eva:power-outline" />}
                      onClick={onDeactivateSelected}
                      sx={{
                        bgcolor: 'white',
                        color: 'text.primary',
                        borderColor: 'grey.300',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          borderColor: 'grey.400',
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Deactivate
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Icon icon="eva:trash-2-fill" />}
                      onClick={onDeleteSelected}
                      sx={{
                        bgcolor: 'white',
                        color: 'error.main',
                        borderColor: 'grey.300',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          borderColor: 'grey.400',
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Delete
                    </Button>
                  </Stack>
                )}

                <Box sx={{ display: 'flex', gap: 1, marginLeft: 'auto' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Icon icon={downloadFill} />}
                    onClick={onExportSelected}
                    sx={{
                      bgcolor: 'white',
                      color: 'text.primary',
                      borderColor: 'grey.300',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        borderColor: 'grey.400',
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Export Selected
                  </Button>
                  <Button
                    variant="outlined"
                    component="label"
                    size="small"
                    startIcon={<Icon icon={uploadFill} />}
                    sx={{
                      bgcolor: 'white',
                      color: 'text.primary',
                      borderColor: 'grey.300',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        borderColor: 'grey.400',
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Import
                    <input
                      type="file"
                      accept=".json"
                      hidden
                      onChange={(event) => {
                        const file = event.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (e) => {
                            try {
                              const jsonData = JSON.parse(e.target.result);
                              onImport?.(jsonData);
                            } catch (error) {
                              console.error('Invalid JSON file format');
                            }
                          };
                          reader.readAsText(file);
                        }
                        event.target.value = '';
                      }}
                    />
                  </Button>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
      )} */}

  {/* Tokens Grid */}
  {!tokens || !tokens.length ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Icon 
            icon="eva:credit-card-outline" 
            style={{ fontSize: '64px', color: '#ccc', marginBottom: '16px' }}
          />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {emptyMessage}
          </Typography>
          <Alert severity="info" sx={{ maxWidth: 400, mx: 'auto' }}>
            <Typography variant="body2">
              No OCPI tokens have been created yet. Tokens will appear here once they are configured.
            </Typography>
          </Alert>
        </Box>
      ) : (
        <Box>
          {/* Results Summary */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {tokens.length} token{tokens.length !== 1 ? 's' : ''}
            </Typography>
            <Chip 
              label={`${tokens.length} total`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          </Box>

          {/* Tokens Grid */}
          {loading ? (
            <Loader variant='skeleton' skeletonVariant='card' skeletonCount={3} />
          ) : (
            <Grid container spacing={3}>
              {tokens.map((token) => (
              <Grid item xs={12} sm={6} md={4} key={token.id || token.uid}>
                <Fade in timeout={300}>
                  <Box>
                    <TokenCard
                      token={token}
                      selected={selectedTokenIds.includes(token.id || token.uid)}
                      onSelect={() => onTokenSelect?.(token.id || token.uid)}
                      onView={() => onTokenView?.(token.id || token.uid)}
                      onEdit={() => onTokenEdit?.(token.id || token.uid)}
                      onDelete={() => onTokenDelete?.(token.id || token.uid)}
                    />
                  </Box>
                </Fade>
              </Grid>
            ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small">
              <Select
                value={pagination.limit}
                onChange={handlePageSizeChange}
                sx={{ minWidth: 80 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {`${pagination.offset + 1}-${Math.min(pagination.offset + pagination.limit, pagination.total)} of ${pagination.total}`}
            </Typography>
            <Pagination
              count={totalPages}
              page={Math.floor(pagination.offset / pagination.limit) + 1}
              onChange={handlePageChange}
              color="primary"
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default TokenList; 